import { FormikField } from "@components/forms/FormikField/FormikField";
import { PhoneInput } from "@components/phoneNumber/PhoneInput";
import type { InputSize } from "@helpers/form";
import type { FieldPath } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import type { KitFieldProps } from "./kitFieldProps.types";

type PhoneFieldValue = string | null | undefined;

/** Paths of `Values` that hold a phone number's digits. */
export type PhoneFieldPath<Values> = FieldPath<Values, PhoneFieldValue, string>;

export type PhoneFieldProps<Name extends string> = KitFieldProps<
    Name,
    HTMLInputElement,
    string
> & {
    readOnly?: boolean;
    size?: InputSize;
};

/**
 * US phone number field bound to a typed Formik path. The form holds the
 * digits only; the input shows them masked as `(###) ###-####`.
 */
export const PhoneField = <
    Values,
    Name extends PhoneFieldPath<Values> = PhoneFieldPath<Values>,
>(
    props: PhoneFieldProps<Name>,
) => {
    const { name, ref, disabled, readOnly, required, onValueChange, size } =
        props;

    return (
        <FormikField<PhoneFieldValue> name={name} {...pickChromeProps(props)}>
            {({ field, helpers, invalid }) => (
                <PhoneInput
                    ref={ref}
                    name={name}
                    value={field.value ?? ""}
                    onValueChange={(digits) => {
                        void helpers.setValue(digits);
                        onValueChange?.(digits);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    invalid={invalid}
                    disabled={disabled}
                    readOnly={readOnly}
                    required={required}
                    aria-labelledby={props["aria-labelledby"]}
                    size={size}
                />
            )}
        </FormikField>
    );
};
